// Constants
export const FC_NAME = 'Hairin';
export const COMPANY_NAME = 'Prudential';
export const SCRIPTED_AGENT_ID = 'agent_01jzqbwk0yf9vs71hrgbqxexfr';

export interface ScriptNode {
  id: string;
  type: 'greeting' | 'statement' | 'question' | 'closing' | 'decision';
  content: string;
  agentMessage: string;
  expectedResponse?: {
    positive?: string[];
    negative?: string[];
    neutral?: string[];
  };
  nextNodes?: {
    positive?: string;
    negative?: string;
    neutral?: string;
    default?: string;
  };
  isEnd?: boolean;
  backgroundColor?: string;
  requiresInput?: boolean;
  waitForResponse?: boolean;
}

export interface Script {
  id: string;
  name: string;
  description: string;
  startNode: string;
  nodes: Record<string, ScriptNode>;
  agentInfo?: {
    name: string;
    voiceId: string;
    avatar?: string;
  };
}

export const prudentialAnnualCheckInScript: Script = {
  id: 'prudential-annual-checkin',
  name: 'Enhanced Annual Check-In',
  description: 'Prudential annual policy review script with decision flow',
  startNode: '1',
  agentInfo: {
    name: 'Rachel',
    voiceId: 'ckdz71REaQKVx2gnOQjQ',
    avatar: 'https://dopmo1eihgbgm.cloudfront.net/67135d6f890cc59eb99e65f9/ai-headshot.webp'
  },
  nodes: {
    '1': {
      id: '1',
      type: 'greeting',
      content: 'Initial greeting and introduction',
      agentMessage: `Hi Good Morning/Afternoon/Evening. This is Rachel calling from ${COMPANY_NAME}, on behalf of your financial consultant, ${FC_NAME}. I would like to inform you that I'm a virtual assistant and share that this call is recorded for training and quality purposes. Do you have a moment to speak?`,
      backgroundColor: '#d4edda',
      requiresInput: true,
      waitForResponse: true,
      expectedResponse: {
        positive: ['yes', 'yeah', 'sure', 'okay', 'go ahead', 'fine', 'i have time'],
        negative: ['no', 'busy', 'not now', 'later', 'bad time'],
      },
      nextNodes: {
        positive: '2',
        negative: '7',
        neutral: '10', // For hearing issues
        default: '2'
      }
    },
    '2': {
      id: '2',
      type: 'statement',
      content: 'Explain purpose and ask for FC contact',
      agentMessage: `It's been awhile, hope you're doing well. I am calling to see if you would like to catch up to review and get an update of your policies as you plan for your next milestones. Would you be interested to have ${FC_NAME} contact you?`,
      backgroundColor: '#ffffff',
      requiresInput: true,
      waitForResponse: true,
      expectedResponse: {
        positive: ['yes', 'yeah', 'sure', 'okay', 'interested', 'sounds good'],
        negative: ['no', 'not interested', 'no need', 'not necessary'],
      },
      nextNodes: {
        positive: '4',  // If interested, go straight to callback timing
        negative: '11',  // If not interested, explain team approach
        neutral: '11',   // For unclear responses, explain team approach
        default: '11'
      }
    },
    '3': {
      id: '3',
      type: 'statement',
      content: 'Address hesitations and explain process',
      agentMessage: `I understand that you may have hesitations due to various reasons. ${FC_NAME} will work with you to review your current portfolio according to your needs or discuss nomination of beneficiaries for your policies. If you find that it makes sense, ${FC_NAME} can work together with you on it, if not, let's keep in touch until a more suitable time. May I arrange for ${FC_NAME} to get in touch with you to arrange an appointment?`,
      backgroundColor: '#ffffff',
      requiresInput: true,
      waitForResponse: true,
      expectedResponse: {
        positive: ['yes', 'okay', 'fine', 'arrange', 'sure'],
        negative: ['no', 'not now', 'maybe later'],
      },
      nextNodes: {
        positive: '4',
        negative: '9',
        default: '4'
      }
    },
    '4': {
      id: '4',
      type: 'statement',
      content: 'Confirm callback timing',
      agentMessage: `Thank you, ${FC_NAME} will give you a call within the next 2 working days, is that alright with you?`,
      backgroundColor: '#d4edda',
      requiresInput: true,
      waitForResponse: true,
      expectedResponse: {
        positive: ['yes', 'okay', 'fine', 'alright', 'sounds good', 'that\'s alright', 'sure'],
        negative: ['no', 'too soon', 'different time', 'another time', 'later', 'not good'],
      },
      nextNodes: {
        positive: '5',  // ALWAYS ask for timing, even when they agree
        negative: '5',  // Ask for timing
        neutral: '5',   // Ask for timing
        default: '5'    // Always go to step 5
      }
    },
    '5': {
      id: '5',
      type: 'question',
      content: 'Ask for preferred timing',
      agentMessage: `Oh, when would be a good time to call?`,
      backgroundColor: '#ffffff',
      requiresInput: true,
      waitForResponse: true,
      nextNodes: {
        default: '6'
      }
    },
    '6': {
      id: '6',
      type: 'closing',
      content: 'Positive closing',
      agentMessage: `Great, ${FC_NAME} will get in touch with you then. Thanks for your time, and have a nice day. Bye!`,
      backgroundColor: '#ffcccc',
      isEnd: true,
      requiresInput: false
    },
    '7': {
      id: '7',
      type: 'question',
      content: 'Handle FC callback offer',
      agentMessage: `Ah.. Sorry for disturbing you. Would it be ok if ${FC_NAME} gives you a call later?`,
      backgroundColor: '#ffffcc',
      requiresInput: true,
      waitForResponse: true,
      expectedResponse: {
        positive: ['yes', 'okay', 'fine', 'sure', 'another day'],
        negative: ['no', 'don\'t call', 'not interested'],
      },
      nextNodes: {
        positive: '5',  // When they agree to callback, go straight to asking for time
        negative: '8',
        default: '8'
      }
    },
    '8': {
      id: '8',
      type: 'closing',
      content: 'Neutral closing',
      agentMessage: `Oh okay, no worries. Thanks for speaking with me today, bye!`,
      backgroundColor: '#ffcccc',
      isEnd: true,
      requiresInput: false
    },
    '9': {
      id: '9',
      type: 'closing',
      content: 'Simple closing',
      agentMessage: `Thank you, and have a good day. Bye.`,
      backgroundColor: '#ffcccc',
      isEnd: true,
      requiresInput: false
    },
    '10': {
      id: '10',
      type: 'closing',
      content: 'Hearing issue closing',
      agentMessage: `Sorry I am having some trouble hearing you. I'll call you back another time. Have a nice day. Bye!`,
      backgroundColor: '#ffcccc',
      isEnd: true,
      requiresInput: false
    },
    '11': {
      id: '11',
      type: 'statement',
      content: 'Tag team explanation',
      agentMessage: `I appreciate your question, ${FC_NAME} and I work on a tag team effort basis, to ensure you get timely attention. Our approach is to be as efficient and as responsive as possible in assisting you. Rest assured, I can get ${FC_NAME} to contact you within 2 working days. Would you be interested?`,
      backgroundColor: '#ffffcc',
      requiresInput: true,
      waitForResponse: true,
      expectedResponse: {
        positive: ['yes', 'okay', 'interested', 'sure'],
        negative: ['no', 'not interested'],
      },
      nextNodes: {
        positive: '4',  // When they agree after team explanation, ask about callback timing
        negative: '9',  // If not interested, end politely
        default: '4'
      }
    }
  }
};